import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Video,
  Plus,
  ExternalLink,
  Play,
  Trash2,
  Calendar,
  RefreshCw,
  Film,
  Sparkles,
  Globe,
  Link as LinkIcon,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICES_GRATUITS = [
  {
    nom: "CapCut",
    description: "Montage vidéo gratuit avec IA - Sous-titres auto, effets, templates",
    url: "https://www.capcut.com/fr-fr/",
    couleur: "#00e5ff",
    tag: "Montage IA",
  },
  {
    nom: "Canva Vidéo",
    description: "Créez des vidéos marketing avec des templates professionnels",
    url: "https://www.canva.com/fr_fr/creer/video/",
    couleur: "#7c3aed",
    tag: "Templates",
  },
  {
    nom: "InVideo AI",
    description: "Générez des vidéos à partir d'un texte avec l'IA",
    url: "https://invideo.io/",
    couleur: "#3b82f6",
    tag: "Texte vers vidéo",
  },
  {
    nom: "Clipchamp (Microsoft)",
    description: "Éditeur vidéo gratuit de Microsoft - Facile et complet",
    url: "https://clipchamp.com/fr/",
    couleur: "#10b981",
    tag: "Gratuit Microsoft",
  },
];

export default function VideoHubPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({ titre: '', description: '', url: '', service: '' });

  const getHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'X-Societe-Id': localStorage.getItem('activeSocieteId') || '',
    'Content-Type': 'application/json'
  }), []);

  const fetchVideos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/heygen/videos`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setVideos(data.videos || []);
      }
    } catch (error) {
      console.error('Erreur chargement vidéos:', error);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const handleSaveVideo = async () => {
    if (!formData.titre || !formData.url) {
      toast.error('Titre et URL sont requis');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/heygen/videos`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          title: formData.titre,
          description: formData.description,
          video_url: formData.url,
          service: formData.service || 'autre',
        })
      });
      if (res.ok) {
        toast.success('Vidéo enregistrée');
        setShowAddDialog(false);
        setFormData({ titre: '', description: '', url: '', service: '' });
        fetchVideos();
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Supprimer cette vidéo ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/heygen/videos/${videoId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (res.ok) {
        toast.success('Vidéo supprimée');
        fetchVideos();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="video-hub-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-pink-500 to-violet-600 rounded-xl text-white">
            <Video className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hub Vidéo</h1>
            <p className="text-gray-500">Créez vos vidéos marketing avec des outils gratuits</p>
          </div>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-violet-600 hover:bg-violet-700"
          data-testid="add-video-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Enregistrer une vidéo
        </Button>
      </div>

      {/* Services gratuits */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" />
          Outils de création gratuits
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICES_GRATUITS.map((service) => (
            <Card
              key={service.nom}
              className="hover:shadow-lg transition-shadow cursor-pointer group"
              data-testid={`service-${service.nom.toLowerCase().replace(/\s/g, '-')}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                    style={{ backgroundColor: service.couleur }}
                  >
                    {service.nom[0]}
                  </div>
                  <Badge variant="secondary" className="text-xs">{service.tag}</Badge>
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{service.nom}</h3>
                <p className="text-sm text-gray-500 mb-3">{service.description}</p>
                <a
                  href={service.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-violet-600 hover:text-violet-800 group-hover:underline"
                  data-testid={`open-${service.nom.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <Globe className="w-4 h-4" />
                  Ouvrir le site
                  <ExternalLink className="w-3 h-3" />
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Mes vidéos */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Film className="w-5 h-5 text-violet-500" />
          Mes vidéos ({videos.length})
        </h2>
        {videos.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border" data-testid="no-videos">
            <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucune vidéo enregistrée</h3>
            <p className="text-gray-500 mb-4">Créez une vidéo avec un outil ci-dessus, puis enregistrez le lien ici</p>
            <Button
              onClick={() => setShowAddDialog(true)}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Enregistrer ma première vidéo
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="videos-grid">
            {videos.map((video, idx) => (
              <Card key={video.id || idx} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 truncate flex-1">{video.title}</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(video.id)}
                      className="text-red-400 hover:text-red-600 -mr-2"
                      data-testid={`delete-video-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {video.description && (
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{video.description}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {video.service && (
                        <Badge variant="outline" className="text-xs">{video.service}</Badge>
                      )}
                      {video.created_at && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(video.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                    {video.video_url && (
                      <a
                        href={video.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-violet-600 hover:text-violet-800"
                        data-testid={`play-video-${idx}`}
                      >
                        <Play className="w-4 h-4" />
                        Voir
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog ajout vidéo */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md" data-testid="add-video-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-violet-600" />
              Enregistrer une vidéo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Titre *</label>
              <Input
                value={formData.titre}
                onChange={(e) => setFormData(prev => ({ ...prev, titre: e.target.value }))}
                placeholder="Ex: Pub EARL Vergers Bio - Printemps 2026"
                data-testid="video-titre-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">URL de la vidéo *</label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://..."
                data-testid="video-url-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Service utilisé</label>
              <select
                value={formData.service}
                onChange={(e) => setFormData(prev => ({ ...prev, service: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="video-service-select"
              >
                <option value="">-- Sélectionner --</option>
                {SERVICES_GRATUITS.map(s => (
                  <option key={s.nom} value={s.nom}>{s.nom}</option>
                ))}
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description de la vidéo..."
                rows={2}
                data-testid="video-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700"
              onClick={handleSaveVideo}
              data-testid="save-video-btn"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
